import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flag, FlagOff, AlertTriangle, CheckCircle2, FileText, ExternalLink, Search,
  MessageSquare, ArrowRight, X, Send, Link2, History as HistoryIcon,
  Bell, Mail, Slack, Paperclip, Pencil, Trash2, FileCheck2, ChevronLeft, ChevronRight, FileClock,
} from 'lucide-react';
import { api } from '../api/sangly';
import { useAuth } from '../contexts/AuthProvider';
import { Dialog } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Select } from './ui/select';
import SortableTableHead from './SortableTableHead';
import MentionTextarea from './MentionTextarea';
import PetanqueLoader from './PetanqueLoader';
import InvoiceDetailDialog from './InvoiceDetailDialog';
import { money, moneyExact, formatNumber, fmtDate, duePhrase, timeAgo, BLOCKED, PROFORMA_STATUS } from '../lib/format';
import { cn } from '../lib/utils';

// business unit shown in the 'Business' select (derived by the sync from the bill accounts)
const UNIT_LABEL = { lamptwist: 'LampTwist', calum: 'Calum', shared: 'Shared', unknown: 'LampTwist (no expense signal)' };

const INV_FILTERS = [
  { key: 'overdue', label: 'Overdue' },
  { key: 'open', label: 'Open' },
  { key: 'all', label: 'All' },
];
const INV_PAGE = 50;   // invoices per page inside the vendor dialog

const yearStart = () => `${new Date().getFullYear()}-01-01`;

const ST_BG = { 1: 'bg-slate-300', 2: 'bg-amber-400', 3: 'bg-rose-500' };
const ST_LABEL = { 1: 'Normal', 2: 'At risk', 3: 'Blocked', 4: 'Proforma' };
const MONTH_MS = 30 * 24 * 3600 * 1000;
// Sangly went live ~9 Jul 2026 - there's no status history before that, so the
// timeline starts here rather than showing empty pre-launch space.
const TOOL_START = new Date(2026, 6, 9).getTime();

const WINDOWS = [{ key: '12m', label: '12m', months: 12 }, { key: '24m', label: '24m', months: 24 }, { key: 'all', label: 'All' }];

// A note attachment. Bytes come from an authenticated endpoint, so images are
// fetched as a blob and shown via an object URL (a plain <img src> wouldn't send
// the auth header); non-images render as a chip that opens on click.
function Attachment({ att }) {
  const isImg = (att.content_type || '').startsWith('image/');
  const [thumb, setThumb] = useState(null);
  useEffect(() => {
    if (!isImg) return undefined;
    let objUrl; let cancelled = false;
    api.suppliers.attachmentBlob(att.id)
      .then((blob) => { if (!cancelled) { objUrl = URL.createObjectURL(blob); setThumb(objUrl); } })
      .catch(() => {});
    return () => { cancelled = true; if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [att.id, isImg]);
  const openFull = async () => {
    try {
      const blob = await api.suppliers.attachmentBlob(att.id);
      window.open(URL.createObjectURL(blob), '_blank', 'noopener');
    } catch { /* ignore */ }
  };
  if (isImg) {
    return (
      <button type="button" onClick={openFull} title={att.name}
        className="h-16 w-16 overflow-hidden rounded-md border border-border bg-muted transition hover:opacity-90">
        {thumb
          ? <img src={thumb} alt={att.name} className="h-full w-full object-cover" />
          : <span className="block h-full w-full animate-pulse bg-muted" />}
      </button>
    );
  }
  return (
    <button type="button" onClick={openFull} title={att.name}
      className="inline-flex max-w-[220px] items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-[12px] hover:bg-muted">
      <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
      <span className="truncate">{att.name || 'attachment'}</span>
    </button>
  );
}

function CommentSnapshot({ snapshot }) {
  if (!snapshot || Object.keys(snapshot).length === 0) return null;
  const status = BLOCKED[snapshot.account_status] || BLOCKED[1];
  const items = [
    ['Open', formatNumber(snapshot.open_count)],
    ['Overdue', formatNumber(snapshot.overdue_count)],
    ['Due ≤7d', formatNumber(snapshot.due_soon_count)],
    ['Last paid', fmtDate(snapshot.last_payment_date)],
    ['Open amt', money(snapshot.open_amount)],
    ['Overdue amt', money(snapshot.overdue_amount)],
  ];

  return (
    <div
      className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 border-t border-border/70 pt-2 text-[11px] text-muted-foreground"
      title="Snapshot captured when this note was posted"
    >
      <span className="font-medium text-foreground/80">Snapshot {fmtDate(snapshot.date)}</span>
      <Badge variant={status.variant} className="text-[10px]">{status.label}</Badge>
      {snapshot.is_following && <Badge variant="accent" className="text-[10px]">Following</Badge>}
      {items.map(([label, value]) => (
        <span key={label}>
          {label} <span className="font-medium text-foreground/80 tnum">{value}</span>
        </span>
      ))}
    </div>
  );
}

function snapshotFromSupplier(sup) {
  if (!sup) return {};
  return {
    date: new Date().toISOString().slice(0, 10),
    account_status: sup.account_status,
    is_following: sup.is_following,
    open_count: sup.open_count,
    overdue_count: sup.overdue_count,
    due_soon_count: sup.due_soon_count,
    last_payment_date: sup.last_payment_date,
    open_amount: sup.open_amount,
    overdue_amount: sup.overdue_amount,
  };
}

// Horizontal status timeline: colored segments for the status held over each
// period, a tick at each change, and faint month/quarter/year gridlines.
function StatusTimeline({ history, compact = false }) {
  const [win, setWin] = useState('12m');
  const now = Date.now();
  const asc = [...history].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const earliest = asc.length ? new Date(asc[0].created_at).getTime() : now - 12 * MONTH_MS;
  const rawStart = win === 'all'
    ? Math.min(earliest - 15 * 24 * 3600 * 1000, now - 3 * MONTH_MS)
    : now - (WINDOWS.find((w) => w.key === win)?.months || 12) * MONTH_MS;
  // Never show empty space before the tool existed.
  const start = Math.max(TOOL_START, rawStart);
  const span = now - start;

  const statusAt = (t) => {
    let s = asc.length ? asc[0].from_status : 1;
    for (const c of asc) { if (new Date(c.created_at).getTime() <= t) s = c.to_status; else break; }
    return s;
  };
  const changeTimes = asc.map((c) => new Date(c.created_at).getTime()).filter((t) => t > start && t < now);
  const bounds = [start, ...changeTimes, now];
  const segments = [];
  for (let i = 0; i < bounds.length - 1; i += 1) {
    const [t0, t1] = [bounds[i], bounds[i + 1]];
    if (t1 > t0) segments.push({ st: statusAt((t0 + t1) / 2), width: ((t1 - t0) / span) * 100 });
  }

  // gridlines: monthly if the window is short, else quarterly / yearly
  const monthsSpan = span / MONTH_MS;
  const step = monthsSpan <= 18 ? 1 : monthsSpan <= 40 ? 3 : 12;
  const grid = [];
  let g = new Date(start);
  g = new Date(g.getFullYear(), g.getMonth() + (g.getDate() > 1 ? 1 : 0), 1);
  while (g.getTime() < now) {
    if (step === 1 || g.getMonth() % step === 0) grid.push(g.getTime());
    g = new Date(g.getFullYear(), g.getMonth() + 1, 1);
  }
  const fmt = (t) => new Date(t).toLocaleDateString('nl-BE', { month: 'short', year: '2-digit' });

  const bar = (h) => (
    <div className={cn('relative flex overflow-hidden rounded-full border border-border', h)}>
      {segments.map((seg, i) => (
        <div key={i} className={cn('h-full', ST_BG[seg.st])} style={{ width: `${seg.width}%` }} title={ST_LABEL[seg.st]} />
      ))}
      {grid.map((t) => (
        <div key={`g${t}`} className="absolute top-0 h-full w-px bg-white/35"
             style={{ left: `${((t - start) / span) * 100}%` }} />
      ))}
      {asc.filter((c) => new Date(c.created_at).getTime() > start).map((c) => {
        const left = ((new Date(c.created_at).getTime() - start) / span) * 100;
        return (
          <div key={c.id} className="absolute top-0 h-full w-[1.5px] bg-foreground/60"
               style={{ left: `${Math.min(99.6, Math.max(0.4, left))}%` }}
               title={`${c.actor_name}: ${ST_LABEL[c.from_status]} → ${ST_LABEL[c.to_status]} · ${new Date(c.created_at).toLocaleDateString('nl-BE')}`} />
        );
      })}
    </div>
  );

  const winToggle = (sz) => (
    <div className="flex shrink-0 gap-0.5">
      {WINDOWS.map((w) => (
        <button key={w.key} onClick={() => setWin(w.key)}
          className={cn('rounded border font-medium transition-colors', sz,
            win === w.key ? 'border-primary bg-primary/12 text-foreground' : 'border-border text-muted-foreground hover:bg-muted')}>
          {w.label}
        </button>
      ))}
    </div>
  );

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <span className="shrink-0 text-[11px] font-medium text-muted-foreground">Status history</span>
        <div className="flex-1">{bar('h-2.5')}</div>
        {winToggle('px-1 py-0 text-[9px]')}
      </div>
    );
  }

  return (
    <div>
      <div className="mb-1.5 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
        <span>Status history</span>
        <div className="flex gap-2.5">
          {[1, 2, 3].map((s) => (
            <span key={s} className="inline-flex items-center gap-1">
              <span className={cn('h-2 w-2 rounded-sm', ST_BG[s])} />{ST_LABEL[s]}
            </span>
          ))}
        </div>
        <div className="ml-auto">{winToggle('px-1.5 py-0.5 text-[10px]')}</div>
      </div>
      {bar('h-3.5')}
      <div className="mt-1 flex justify-between text-[10px] text-muted-foreground">
        <span>{fmt(start)}</span><span>now</span>
      </div>
    </div>
  );
}

export default function SupplierDetailDialog({ supplierId, open, scope, since, onClose, onChanged, users = [] }) {
  const focusSince = since || yearStart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sup, setSup] = useState(null);
  const [proformas, setProformas] = useState([]);   // Sangly-only pro-formas linked to this vendor
  const [payStats, setPayStats] = useState(null);
  const [followOpen, setFollowOpen] = useState(false);
  const [followPrefs, setFollowPrefs] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invLoading, setInvLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [invoiceId, setInvoiceId] = useState(null);
  const invReq = useRef(0);   // guards against out-of-order invoice fetches
  // Which invoices to show inside the modal - inherits the list filter it was
  // opened from (e.g. "Has overdue" → Overdue), and is switchable.
  const [invFilter, setInvFilter] = useState('open');
  const [ordering, setOrdering] = useState('priority');
  const [q, setQ] = useState('');   // search within this vendor's invoices
  const [invOffset, setInvOffset] = useState(0);   // pagination within the invoices tab
  const [invCount, setInvCount] = useState(0);     // total matching (from the server)
  const [selectedInv, setSelectedInv] = useState(() => new Set());   // ticked for bulk proofs
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkMsg, setBulkMsg] = useState('');
  // vendor-level activity: notes + status-change history
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState([]);
  const [noteGorgias, setNoteGorgias] = useState('');
  const [posting, setPosting] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [tab, setTab] = useState('overview');   // overview | invoices
  const [files, setFiles] = useState([]);        // pending attachments for the composer
  const [proformaPrompt, setProformaPrompt] = useState(false);   // nudge to attach the proforma invoice
  const [attachErr, setAttachErr] = useState('');
  const [editingId, setEditingId] = useState(null);   // note being edited
  const [editBody, setEditBody] = useState('');
  const [editFiles, setEditFiles] = useState([]);     // attachments being added while editing
  const [editRemove, setEditRemove] = useState([]);   // existing attachment ids to drop
  const fileInputRef = useRef(null);
  const editFileRef = useRef(null);

  const notes = useMemo(
    () => [...comments].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [comments]
  );
  const NOTES_PREVIEW = 3;
  const visibleNotes = showAllNotes ? notes : notes.slice(0, NOTES_PREVIEW);


  // Search + paging are server-side now, so the fetched page renders as-is.
  const filtered = invoices;

  const loadSupplier = useCallback(async () => {
    if (!supplierId) return;
    setLoading(true);
    setPayStats(null);
    try { setSup(await api.suppliers.get(supplierId)); }
    finally { setLoading(false); }
    api.suppliers.paymentStats(supplierId).then(setPayStats).catch(() => setPayStats(null));
  }, [supplierId]);

  const loadActivity = useCallback(async () => {
    if (!supplierId) return;
    try {
      const [cm, hist, pfs] = await Promise.all([
        api.suppliers.comments(supplierId), api.suppliers.history(supplierId),
        api.proformas.list('all', { supplier: supplierId }).catch(() => [])]);
      setComments(cm || []); setHistory(hist || []); setProformas(pfs || []);
    } catch { /* ignore */ }
  }, [supplierId]);

  const ATTACH_MAX = 10 * 1024 * 1024;
  const ATTACH_OK = /^(image\/(png|jpe?g|gif|webp)|application\/pdf)$/;
  const pickValid = (picked) => {
    const ok = []; let err = '';
    for (const f of picked) {
      if (!ATTACH_OK.test(f.type)) { err = `"${f.name}" isn't an image or PDF.`; continue; }
      if (f.size > ATTACH_MAX) { err = `"${f.name}" is over the 10 MB limit.`; continue; }
      ok.push(f);
    }
    return { ok, err };
  };
  const addFiles = (picked) => {
    const { ok, err } = pickValid(picked);
    setAttachErr(err);
    if (ok.length) setFiles((prev) => [...prev, ...ok]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));
  const addEditFiles = (picked) => {
    const { ok, err } = pickValid(picked);
    setAttachErr(err);
    if (ok.length) setEditFiles((prev) => [...prev, ...ok]);
    if (editFileRef.current) editFileRef.current.value = '';
  };
  const startEdit = (c) => {
    setEditingId(c.id); setEditBody(c.body || '');
    setEditFiles([]); setEditRemove([]); setAttachErr('');
  };
  const cancelEdit = () => {
    setEditingId(null); setEditBody(''); setEditFiles([]); setEditRemove([]); setAttachErr('');
  };

  const postComment = async () => {
    if (!body.trim() && !noteGorgias.trim() && files.length === 0) return;
    setPosting(true);
    try {
      const c = await api.suppliers.addComment(supplierId, body.trim(), mentions, noteGorgias.trim(), files);
      const withSnapshot = Object.keys(c.snapshot || {}).length > 0
        ? c
        : { ...c, snapshot: snapshotFromSupplier(sup) };
      setComments((prev) => [...prev, withSnapshot]);
      setBody(''); setMentions([]); setNoteGorgias(''); setFiles([]); setAttachErr(''); setProformaPrompt(false);
    } finally { setPosting(false); }
  };

  const saveEdit = async (c) => {
    try {
      const updated = await api.suppliers.editComment(c.id,
        { body: editBody.trim(), files: editFiles, remove: editRemove });
      setComments((prev) => prev.map((x) => (x.id === c.id ? { ...x, ...updated } : x)));
    } catch { /* ignore */ }
    cancelEdit();
  };
  const removeNote = async (c) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    setComments((prev) => prev.filter((x) => x.id !== c.id));   // optimistic
    try { await api.suppliers.deleteComment(c.id); } catch { loadActivity(); }
  };

  const loadInvoices = useCallback(async () => {
    if (!supplierId) return;
    const my = ++invReq.current;
    setInvLoading(true);
    try {
      // Scope to the focus window so frozen pre-2026 bills don't clutter the vendor.
      const params = { supplier: supplierId, ordering, limit: INV_PAGE, offset: invOffset,
                       invoice_since: focusSince };
      if (invFilter === 'overdue') params.status = 'overdue';
      else if (invFilter === 'open') params.status = 'open';
      if (q.trim()) params.q = q.trim();
      const inv = await api.invoices.list(params);
      if (my === invReq.current) {   // ignore stale fetches
        setInvoices(inv.results || []);
        setInvCount(inv.count || 0);
      }
    } finally {
      if (my === invReq.current) setInvLoading(false);
    }
  }, [supplierId, invFilter, focusSince, ordering, q, invOffset]);

  // On open: adopt the incoming list scope (default to Open, not All, so paid
  // bills don't clutter the vendor view), then load.
  useEffect(() => {
    if (open) {
      setInvFilter(scope === 'overdue' ? 'overdue' : 'open'); setOrdering('priority'); setQ('');
      setBody(''); setMentions([]); setShowAllNotes(false); setTab('overview');
      setFiles([]); setAttachErr(''); setEditingId(null); setEditBody('');
      setEditFiles([]); setEditRemove([]);
      loadSupplier(); loadActivity();
    }
  }, [open, scope, loadSupplier, loadActivity]);
  useEffect(() => { if (open) loadInvoices(); }, [open, loadInvoices]);
  // a fresh set of invoices (reload / filter change) drops the selection
  useEffect(() => { setSelectedInv(new Set()); setBulkMsg(''); }, [invoices]);
  // "All" shows the most recent bills first; open/overdue keep priority order.
  useEffect(() => { setOrdering(invFilter === 'all' ? '-invoice_date' : 'priority'); }, [invFilter]);
  // any filter / sort / search change → back to the first page
  useEffect(() => { setInvOffset(0); }, [invFilter, ordering, q]);

  const refreshAll = () => { loadSupplier(); loadInvoices(); onChanged?.(); };

  // ---- bulk proof-of-payment for selected paid bills ----
  const paidInvSelectedIds = invoices.filter((r) => selectedInv.has(r.id) && r.payment_state === 'paid').map((r) => r.id);
  const toggleInv = (id) => setSelectedInv((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const allInvSelected = filtered.length > 0 && filtered.every((r) => selectedInv.has(r.id));
  const toggleAllInv = () => setSelectedInv(allInvSelected ? new Set() : new Set(filtered.map((r) => r.id)));
  const genProofs = async () => {
    if (!paidInvSelectedIds.length) return;
    setBulkLoading(true); setBulkMsg('');
    try {
      const { blob, skipped } = await api.invoices.proofs(paidInvSelectedIds);
      window.open(URL.createObjectURL(blob), '_blank');
      if (skipped) setBulkMsg(`${skipped} skipped — no reconciled payment yet`);
    } catch {
      setBulkMsg('Could not generate proofs — are the selected bills paid?');
    } finally { setBulkLoading(false); }
  };

  const setStatus = async (level) => {
    setBusy(true);
    const wasProforma = sup?.account_status === 4;
    try {
      setSup(await api.suppliers.setStatus(supplierId, level)); loadActivity(); onChanged?.();
      // just moved TO proforma: nudge the user to log the proforma invoice as a note
      if (level === 4 && !wasProforma) {
        setTab('overview');
        setProformaPrompt(true);
        setBody((b) => b.trim() ? b : 'Proforma invoice received — to validate and forward to the accountant.');
      } else if (level !== 4) {
        setProformaPrompt(false);
      }
    } finally { setBusy(false); }
  };
  const setReminder = async (date) => {
    setBusy(true);
    try { setSup(await api.suppliers.remind(supplierId, date || null)); onChanged?.(); }
    finally { setBusy(false); }
  };
  const toggleSourced = async () => {
    setBusy(true);
    try { setSup(await api.suppliers.setSourced(supplierId, !sup.force_sourced)); onChanged?.(); }
    finally { setBusy(false); }
  };
  const setUnit = async (value) => {
    setBusy(true);
    try { setSup(await api.suppliers.setUnit(supplierId, value)); onChanged?.(); }
    finally { setBusy(false); }
  };
  const openFollow = () => {
    setFollowPrefs(sup.my_follow
      || { on_payment: true, on_changes: true, on_status: true, on_note: false, via_email: false, via_slack: true });
    setFollowOpen(true);
  };
  const saveFollow = async (prefs) => {
    setBusy(true);
    try { setSup(await api.suppliers.follow(supplierId, prefs)); onChanged?.(); setFollowOpen(false); }
    finally { setBusy(false); }
  };
  const stopFollow = async () => {
    setBusy(true);
    try { setSup(await api.suppliers.unfollow(supplierId)); onChanged?.(); setFollowOpen(false); }
    finally { setBusy(false); }
  };

  const tiles = sup ? [
    ['Invoices 2026', sup.total_count, ''],
    ['Paid', sup.paid_count, '', 'text-emerald-600'],
    ['Open', sup.open_count, money(sup.open_amount)],
    ['Overdue', sup.overdue_count, money(sup.overdue_amount), 'text-rose-600'],
    ['Due ≤ 7 days', sup.due_soon_count, '', 'text-amber-600'],
  ] : [];

  // How we pay this vendor - one verdict, reused as a hero chip + the full band.
  const pb = payStats?.paid_count > 0 ? (() => {
    const d = payStats.avg_delay_days;
    const round = Math.round(d), abs = Math.abs(round);
    return {
      tone: d <= 2 ? 'text-emerald-600' : d <= 10 ? 'text-amber-600' : 'text-rose-600',
      Icon: d <= 2 ? CheckCircle2 : AlertTriangle,
      verdict: d <= -2 ? `We pay ~${abs} days early` : d <= 2 ? 'We pay on time' : `We pay ~${round} days late`,
      short: d <= -2 ? `pays ${abs}d early` : d <= 2 ? 'pays on time' : `pays ${round}d late`,
    };
  })() : null;

  // Follow button + preferences popover - lives in the hero, opens downward.
  const followControl = sup && (
    <div className="relative">
      <Button variant={sup.is_following ? 'secondary' : 'default'} onClick={openFollow} disabled={busy}
        title="Follow this vendor (personal spotlight) and choose what to be notified about.">
        {sup.is_following ? <><Flag className="h-4 w-4" /> Following</> : <><Flag className="h-4 w-4" /> Follow vendor</>}
        {sup.follower_count > 0 && (
          <span className="ml-1 rounded-full bg-primary/15 px-1.5 text-[11px] text-primary">{sup.follower_count}</span>
        )}
      </Button>
      {followOpen && followPrefs && (
        <div className="absolute right-0 top-full z-30 mt-1 w-72 rounded-lg border border-border bg-popover p-3 shadow-pop">
          <div className="mb-1 text-[12px] font-semibold">Notify me about</div>
          {[['on_payment', 'Payments'], ['on_changes', 'Invoice changes (due / amount)'],
            ['on_status', 'Status changes'], ['on_note', 'New notes']].map(([k, lbl]) => (
            <label key={k} className="flex cursor-pointer items-center gap-2 py-1 text-[13px]">
              <input type="checkbox" checked={followPrefs[k]}
                     onChange={(e) => setFollowPrefs((p) => ({ ...p, [k]: e.target.checked }))}
                     className="h-3.5 w-3.5 rounded border-border accent-[hsl(var(--primary))]" />
              {lbl}
            </label>
          ))}
          {/* WHERE to deliver: the in-app bell always records; email + Slack toggle. */}
          <div className="mt-2 border-t border-border pt-2">
            <div className="mb-1 text-[12px] font-semibold">Deliver to</div>
            <div className="flex flex-wrap gap-1.5">
              <span className="inline-flex items-center gap-1 rounded-md border border-primary bg-primary/12 px-2 py-1 text-[11px] font-medium text-foreground"
                    title="The in-app bell always records your notifications.">
                <Bell className="h-3 w-3" /> In-app
              </span>
              {[['via_email', 'Email', Mail], ['via_slack', 'Slack', Slack]].map(([k, lbl, Icon]) => {
                const slackOff = k === 'via_slack' && user?.slack_enabled === false;
                return (
                  <button key={k} type="button" onClick={() => setFollowPrefs((p) => ({ ...p, [k]: !p[k] }))}
                    title={slackOff ? 'Slack isn\'t connected yet - this won\'t deliver until an admin sets it up.' : undefined}
                    className={cn('inline-flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] font-medium transition-colors',
                      followPrefs[k] ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
                    <Icon className="h-3 w-3" /> {lbl}
                    {slackOff && followPrefs[k] && <span className="text-[10px] text-amber-600">·off</span>}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <Button size="sm" onClick={() => saveFollow(followPrefs)} disabled={busy}>
              {sup.is_following ? 'Update' : 'Follow'}
            </Button>
            {sup.is_following && (
              <button onClick={stopFollow} disabled={busy}
                className="inline-flex items-center gap-1 text-[12px] text-rose-600 hover:underline">
                <FlagOff className="h-3.5 w-3.5" /> Stop
              </button>
            )}
            <button onClick={() => setFollowOpen(false)} className="ml-auto text-[12px] text-muted-foreground hover:underline">Close</button>
          </div>
          {sup.followers?.length > 0 && (
            <div className="mt-2 border-t border-border pt-1.5 text-[11px] text-muted-foreground">
              Following: {sup.followers.join(', ')}
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} className="max-w-5xl p-0">
        {loading || !sup ? (
          <div className="grid h-64 place-items-center"><PetanqueLoader size={38} roll /></div>
        ) : (
          <div className="max-h-[calc(100dvh-1rem)] overflow-y-auto sm:max-h-[85vh]">
            {/* hero: identity · payment behaviour · primary actions (all up top) */}
            <div className="border-b border-border p-4 pr-10 sm:p-5 sm:pr-11">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="truncate text-lg font-semibold">{sup.name || '-'}</span>
                    <Badge variant={(BLOCKED[sup.account_status] || BLOCKED[1]).variant} className="shrink-0">
                      {(BLOCKED[sup.account_status] || BLOCKED[1]).label}
                    </Badge>
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[13px] text-muted-foreground">
                    <span>{sup.open_count} open · {money(sup.open_amount)}</span>
                    <span className="opacity-50">·</span>
                    <span>last paid {fmtDate(sup.last_payment_date)}</span>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button onClick={toggleSourced} disabled={busy}
                    title={sup.force_sourced
                      ? 'Forced to Sourced. Click to revert to automatic (based on POs in Odoo).'
                      : 'Treat as a sourced vendor even if the billing entity has no PO in Odoo (e.g. distributor vs brand).'}
                    className={cn('inline-flex h-9 items-center gap-1.5 rounded-md border px-3 text-[13px] font-medium transition-colors',
                      sup.force_sourced ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
                    {sup.force_sourced ? 'Sourced' : 'Mark as sourced'}
                  </button>
                  {followControl}
                </div>
              </div>
              {/* payment behaviour - kept in the top block, next to the vendor's key info */}
              {pb && (
                <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-lg border border-border bg-muted/40 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <pb.Icon className={cn('h-4 w-4', pb.tone)} />
                    <span className={cn('text-[14px] font-semibold', pb.tone)}>{pb.verdict}</span>
                    <span className="text-[12px] text-muted-foreground">vs due date</span>
                  </div>
                  <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-muted-foreground">
                    <span>On time <b className="text-foreground tnum">{payStats.on_time_rate}%</b></span>
                    <span>Avg to pay <b className="text-foreground tnum">{payStats.avg_days_to_pay}d</b>
                      {payStats.avg_terms_days != null && <span> / {payStats.avg_terms_days}d terms</span>}</span>
                    <span><b className="text-foreground tnum">{payStats.paid_count}</b> paid · {money(payStats.total_paid)}</span>
                  </div>
                </div>
              )}
            </div>

            {/* issue banner */}
            {sup.issue_open && (
              <div className={cn('flex items-start gap-2 border-b border-border px-5 py-3 text-[13px]',
                sup.resolved_notified ? 'bg-emerald-500/10' : 'bg-amber-500/10')}>
                {sup.resolved_notified
                  ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                  : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
                <div>
                  {sup.resolved_notified ? (
                    <><span className="font-medium">Looks resolved.</span> The majority of the {sup.snapshot_count} overdue
                    invoices from when this issue was raised are now paid - reset the status below once confirmed.</>
                  ) : (
                    <><span className="font-medium">Open issue.</span> Snapshotted {sup.snapshot_count} overdue invoices
                    {sup.issue_opened_at ? ` ${timeAgo(sup.issue_opened_at)}` : ''}
                    {sup.issue_owner_name ? ` · raised by ${sup.issue_owner_name}` : ''}.</>
                  )}
                </div>
              </div>
            )}

            {/* tab bar: an Overview (the crucial info) + the Invoices list */}
            <div className="sticky top-0 z-20 flex gap-1 border-b border-border bg-card px-4 sm:px-5">
              {[['overview', 'Overview', 0], ['invoices', 'Invoices', invCount]].map(([key, lbl, count]) => (
                <button key={key} onClick={() => setTab(key)}
                  className={cn('-mb-px flex items-center gap-1.5 border-b-2 px-3 py-2.5 text-[13px] font-medium transition-colors',
                    tab === key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground')}>
                  {lbl}
                  {count > 0 && (
                    <span className="rounded-full bg-primary/15 px-1.5 py-px text-[11px] font-semibold tabular-nums text-primary">{count}</span>
                  )}
                </button>
              ))}
            </div>

            {/* OVERVIEW tab: KPIs · status history · how we pay · controls · activity */}
            {tab === 'overview' && (
            <div>
              {/* aggregate tiles */}
              <div className="grid grid-cols-2 gap-px border-b border-border bg-border sm:grid-cols-5">
                {tiles.map(([k, v, sub, tone]) => (
                  <div key={k} className="bg-card px-3 py-3 sm:px-4">
                    <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{k}</div>
                    <div className={cn('mt-0.5 text-xl font-semibold tnum', tone)}>{formatNumber(v)}</div>
                    {sub && <div className="text-[11px] text-muted-foreground">{sub}</div>}
                  </div>
                ))}
              </div>

              {/* pro-formas logged for this vendor (Sangly-only, see /proformas) */}
              {proformas.length > 0 && (
                <div className="border-b border-border px-4 py-3 sm:px-5">
                  <div className="mb-1.5 flex items-center gap-1.5 text-[12px] font-medium">
                    <FileClock className="h-3.5 w-3.5 text-muted-foreground" /> Pro-forma
                    <span className="text-muted-foreground">· {proformas.length}</span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {proformas.map((pf) => {
                      const st = PROFORMA_STATUS[pf.status] || PROFORMA_STATUS.pending;
                      return (
                        <button key={pf.id} type="button" onClick={() => navigate(`/proformas/${pf.id}`)}
                                title="Open the pro-forma"
                                className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1 text-[12px] hover:bg-muted">
                          <span className="font-semibold tnum">{moneyExact(pf.amount, pf.currency)}</span>
                          {pf.reference && <span className="font-mono text-muted-foreground">{pf.reference}</span>}
                          <Badge variant={st.variant}>{st.label}</Badge>
                          {pf.due_date && pf.status === 'pending' && (
                            <span className="text-muted-foreground">by {fmtDate(pf.due_date)}</span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* status history (the component self-labels) */}
              {history.length > 0 && (
                <div className="border-b border-border px-4 py-3 sm:px-5">
                  <StatusTimeline history={history} compact />
                </div>
              )}

              {/* controls */}
              <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:flex-wrap sm:items-end sm:gap-4 sm:p-5">
                <div className="w-full sm:w-auto">
                  <label className="mb-1 block text-[12px] font-medium">Account status (vendor level)</label>
                  <Select className="h-9 w-full sm:w-56" value={sup.account_status} disabled={busy}
                          onChange={(e) => setStatus(Number(e.target.value))}>
                    <option value={1}>Normal</option>
                    <option value={2}>At risk</option>
                    <option value={3}>Account blocked</option>
                    <option value={4}>Proforma</option>
                  </Select>
                </div>
                <div className="w-full sm:w-auto" title="Which business this vendor belongs to. Automatic = derived from the accounts on its bills (Calum / Horeca accounts → Calum).">
                  <label className="mb-1 block text-[12px] font-medium">Business</label>
                  <Select className="h-9 w-full sm:w-52" value={sup.business_unit_override || ''} disabled={busy}
                          onChange={(e) => setUnit(e.target.value)}>
                    <option value="">Automatic · {UNIT_LABEL[sup.business_unit] || sup.business_unit}</option>
                    <option value="lamptwist">LampTwist</option>
                    <option value="calum">Calum</option>
                    <option value="shared">Shared (both)</option>
                  </Select>
                </div>
                <div className="w-full sm:w-auto">
                  <label className="mb-1 block text-[12px] font-medium">Last reminder sent</label>
                  <div className="flex gap-2">
                    <Input type="date" className="h-9 w-full sm:w-44" disabled={busy}
                           value={sup.last_reminder_date || ''}
                           onChange={(e) => setReminder(e.target.value)} />
                    <Button variant="secondary" size="sm" className="h-9 shrink-0" disabled={busy}
                            onClick={() => setReminder('')} title="Mark a reminder sent today">
                      Today
                    </Button>
                  </div>
                </div>
                {busy && <PetanqueLoader size={18} />}
              </div>

              {/* vendor activity & notes */}
              <div className="p-4 sm:p-5">
              <div className="mb-3 flex items-center gap-2">
                <span className="text-[13px] font-semibold">Vendor activity & notes</span>
                {notes.length > 0 && (
                  <span className="rounded-full bg-primary/15 px-1.5 py-px text-[11px] font-semibold tabular-nums text-primary">{notes.length}</span>
                )}
                {sup.status_change_count >= 3 && (
                  <Badge variant="warning"><HistoryIcon className="h-3 w-3" /> {sup.status_change_count} status changes</Badge>
                )}
              </div>

              {/* composer */}
              <div className={cn('rounded-lg border p-3', proformaPrompt ? 'border-sky-300 ring-1 ring-sky-200' : 'border-border')}>
                {proformaPrompt && (
                  <div className="mb-2 flex items-start gap-2 rounded-md border border-sky-200 bg-sky-500/10 p-2.5 text-[12px] text-sky-800">
                    <FileText className="mt-0.5 h-4 w-4 shrink-0 text-sky-600" />
                    <div className="min-w-0 flex-1">
                      <span className="font-medium">Marked Proforma.</span> Attach the proforma invoice the supplier sent and post it as a note — so it&apos;s logged before you validate it and forward it to the accountant.
                      <div className="mt-1.5">
                        <button type="button" onClick={() => fileInputRef.current?.click()}
                                className="inline-flex h-7 items-center gap-1 rounded-md border border-sky-300 bg-card px-2 font-medium text-sky-700 hover:bg-sky-500/10">
                          <Paperclip className="h-3.5 w-3.5" /> Attach the proforma
                        </button>
                      </div>
                    </div>
                    <button type="button" onClick={() => setProformaPrompt(false)} title="Dismiss"
                            className="shrink-0 text-sky-500 hover:text-sky-700"><X className="h-3.5 w-3.5" /></button>
                  </div>
                )}
                <MentionTextarea rows={2} placeholder="Add a note about this vendor - type @ to mention a colleague…"
                                 value={body} onChange={setBody} users={users}
                                 onMention={(u) => setMentions((prev) => prev.some((m) => m.id === u.id) ? prev : [...prev, u])} />
                {/* pending attachments */}
                {files.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {files.map((f, i) => (
                      <span key={`${f.name}-${i}`} className="inline-flex max-w-[220px] items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-[12px]">
                        {f.type.startsWith('image/')
                          ? <Paperclip className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                          : <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                        <span className="truncate">{f.name}</span>
                        <button type="button" onClick={() => removeFile(i)} className="shrink-0 text-muted-foreground hover:text-rose-600">
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {attachErr && <div className="mt-1 text-[12px] text-rose-600">{attachErr}</div>}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {mentions.map((m) => (
                    <Badge key={m.id} variant="accent" className="cursor-pointer"
                           onClick={() => setMentions((prev) => prev.filter((x) => x.id !== m.id))}>
                      @{m.name} <X className="h-3 w-3" />
                    </Badge>
                  ))}
                  <div className="relative w-full sm:w-64">
                    <Link2 className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={noteGorgias} onChange={(e) => setNoteGorgias(e.target.value)}
                           placeholder="Gorgias ticket URL (optional)" className="h-8 pl-7 text-[12px]" />
                  </div>
                  <input ref={fileInputRef} type="file" multiple hidden
                         accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
                         onChange={(e) => addFiles(Array.from(e.target.files || []))} />
                  <button type="button" onClick={() => fileInputRef.current?.click()}
                          title="Attach images or PDFs (max 10 MB each)"
                          className="ml-auto inline-flex h-8 items-center gap-1 rounded-md border border-border bg-card px-2 text-[12px] font-medium text-muted-foreground hover:bg-muted">
                    <Paperclip className="h-3.5 w-3.5" /> Attach
                  </button>
                  <Button size="sm" onClick={postComment}
                          disabled={posting || (!body.trim() && !noteGorgias.trim() && files.length === 0)}>
                    {posting ? <PetanqueLoader size={16} /> : <Send className="h-4 w-4" />} Post
                  </Button>
                </div>
              </div>


              {/* notes - newest first; only the latest few show until expanded */}
              {notes.length > 0 && (
                <ol className="mt-4 space-y-2.5">
                  {visibleNotes.map((c) => (
                    <li key={c.id} className="flex gap-2 sm:gap-3">
                      <span className="mt-0.5 grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/15 text-primary">
                        <MessageSquare className="h-3.5 w-3.5" />
                      </span>
                      <div className="min-w-0 flex-1 rounded-lg border border-border bg-muted/30 px-3 py-2">
                        <div className="flex items-center gap-2">
                          <span className="text-[13px] font-medium">{c.author_name || 'Someone'}</span>
                          <span className="text-[11px] text-muted-foreground">{timeAgo(c.created_at)}{c.edited_at ? ' · edited' : ''}</span>
                          {user?.id && c.author_id === user.id && editingId !== c.id && (
                            <span className="ml-auto flex items-center gap-1">
                              <button onClick={() => startEdit(c)}
                                      title="Edit note" className="text-muted-foreground hover:text-foreground">
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => removeNote(c)} title="Delete note"
                                      className="text-muted-foreground hover:text-rose-600">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          )}
                        </div>
                        {editingId === c.id ? (
                          <div className="mt-1">
                            <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)} rows={2}
                                      className="w-full rounded-md border border-border bg-card px-2 py-1 text-[13px] focus:outline-none focus:ring-1 focus:ring-primary" />
                            {/* current attachments (removable) + newly added ones */}
                            {(c.attachments?.some((a) => !editRemove.includes(a.id)) || editFiles.length > 0) && (
                              <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                {c.attachments?.filter((a) => !editRemove.includes(a.id)).map((a) => (
                                  <span key={a.id} className="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-border bg-card px-2 py-1 text-[12px]">
                                    <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    <span className="truncate">{a.name}</span>
                                    <button type="button" onClick={() => setEditRemove((prev) => [...prev, a.id])}
                                            title="Remove" className="shrink-0 text-muted-foreground hover:text-rose-600"><X className="h-3 w-3" /></button>
                                  </span>
                                ))}
                                {editFiles.map((f, i) => (
                                  <span key={`${f.name}-${i}`} className="inline-flex max-w-[200px] items-center gap-1.5 rounded-md border border-primary/50 bg-primary/8 px-2 py-1 text-[12px]">
                                    <Paperclip className="h-3.5 w-3.5 shrink-0 text-primary" />
                                    <span className="truncate">{f.name}</span>
                                    <button type="button" onClick={() => setEditFiles((prev) => prev.filter((_, j) => j !== i))}
                                            className="shrink-0 text-muted-foreground hover:text-rose-600"><X className="h-3 w-3" /></button>
                                  </span>
                                ))}
                              </div>
                            )}
                            {attachErr && <div className="mt-1 text-[12px] text-rose-600">{attachErr}</div>}
                            <input ref={editFileRef} type="file" multiple hidden
                                   accept="image/png,image/jpeg,image/gif,image/webp,application/pdf"
                                   onChange={(e) => addEditFiles(Array.from(e.target.files || []))} />
                            <div className="mt-1.5 flex items-center gap-2">
                              <Button size="sm" onClick={() => saveEdit(c)}>Save</Button>
                              <button onClick={cancelEdit} className="text-[12px] text-muted-foreground hover:underline">Cancel</button>
                              <button type="button" onClick={() => editFileRef.current?.click()}
                                      className="ml-auto inline-flex items-center gap-1 rounded-md border border-border bg-card px-2 py-1 text-[12px] font-medium text-muted-foreground hover:bg-muted">
                                <Paperclip className="h-3.5 w-3.5" /> Attach
                              </button>
                            </div>
                          </div>
                        ) : (
                          c.body && <div className="mt-0.5 whitespace-pre-wrap text-[13px] leading-relaxed">{c.body}</div>
                        )}
                        {c.gorgias_url && (
                          <a href={c.gorgias_url} target="_blank" rel="noreferrer"
                             className="mt-1 inline-flex items-center gap-1 text-[12px] text-primary hover:underline">
                            <Link2 className="h-3.5 w-3.5" /> Gorgias ticket <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                        {c.attachments?.length > 0 && editingId !== c.id && (
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            {c.attachments.map((a) => <Attachment key={a.id} att={a} />)}
                          </div>
                        )}
                        <CommentSnapshot snapshot={c.snapshot} />
                        {c.mentions?.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {c.mentions.map((m) => <Badge key={m.id} variant="accent">@{m.name}</Badge>)}
                          </div>
                        )}
                      </div>
                    </li>
                  ))}
                </ol>
              )}
              {notes.length > NOTES_PREVIEW && (
                <button onClick={() => setShowAllNotes((v) => !v)}
                        className="mt-2.5 ml-9 inline-flex items-center gap-1 text-[12px] font-medium text-primary hover:underline">
                  {showAllNotes
                    ? 'Show less'
                    : `Show ${notes.length - NOTES_PREVIEW} older note${notes.length - NOTES_PREVIEW === 1 ? '' : 's'}`}
                </button>
              )}
              </div>
            </div>
            )}

            {/* INVOICES tab */}
            {tab === 'invoices' && (
            <div className="p-4 sm:p-5">
              <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
                <span className="text-[13px] font-semibold">
                  {invFilter === 'overdue' ? 'Overdue invoices' : invFilter === 'open' ? 'Open invoices' : 'Invoices'} ({invCount})
                </span>
                <span className="text-[11px] text-muted-foreground">since {String(focusSince).slice(0, 4)}</span>
                <div className="flex w-full flex-col gap-2 sm:ml-auto sm:w-auto sm:flex-row sm:items-center">
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Bill # / ref…"
                           className="h-8 w-full pl-7 text-[12px] sm:w-44" />
                  </div>
                  <div className="flex gap-1">
                    {INV_FILTERS.map((f) => (
                      <button key={f.key} onClick={() => setInvFilter(f.key)}
                        className={cn('rounded-md border px-2 py-0.5 text-[11px] font-medium transition-colors',
                          invFilter === f.key ? 'border-primary bg-primary/12 text-foreground' : 'border-border bg-card text-muted-foreground hover:bg-muted')}>
                        {f.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {selectedInv.size > 0 && (
                <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-lg border border-primary/40 bg-primary/8 px-3 py-1.5">
                  <span className="text-[12px] font-medium">{selectedInv.size} selected</span>
                  {paidInvSelectedIds.length < selectedInv.size && (
                    <span className="text-[11px] text-muted-foreground">· {paidInvSelectedIds.length} paid</span>
                  )}
                  <Button size="sm" onClick={genProofs} disabled={bulkLoading || paidInvSelectedIds.length === 0}
                          title={paidInvSelectedIds.length === 0 ? 'Only paid bills have a proof of payment' : ''}>
                    {bulkLoading ? <PetanqueLoader size={14} /> : <FileCheck2 className="h-4 w-4" />}
                    Proof{paidInvSelectedIds.length === 1 ? '' : 's'} of payment{paidInvSelectedIds.length ? ` (${paidInvSelectedIds.length})` : ''}
                  </Button>
                  {bulkMsg && <span className="text-[11px] text-muted-foreground">{bulkMsg}</span>}
                  <button onClick={() => setSelectedInv(new Set())}
                          className="ml-auto inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                    <X className="h-3.5 w-3.5" /> Clear
                  </button>
                </div>
              )}
              <div className="overflow-x-auto rounded-lg border border-border">
                <table className="min-w-[812px] w-full text-[13px]">
                  <thead className="border-b border-border">
                    <tr>
                      <th className="h-9 w-[34px] px-3 align-middle">
                        <input type="checkbox" aria-label="Select all shown" checked={allInvSelected}
                               ref={(el) => { if (el) el.indeterminate = !allInvSelected && filtered.some((r) => selectedInv.has(r.id)); }}
                               onChange={toggleAllInv}
                               className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-[hsl(var(--primary))]" />
                      </th>
                      <SortableTableHead ordering={ordering} onOrderingChange={setOrdering} asc="invoice" desc="-invoice">
                        Invoice
                      </SortableTableHead>
                      <SortableTableHead ordering={ordering} onOrderingChange={setOrdering} asc="due" desc="-due">
                        Due
                      </SortableTableHead>
                      <SortableTableHead
                        className="text-right"
                        ordering={ordering}
                        onOrderingChange={setOrdering}
                        asc="invoice_amount"
                        desc="-invoice_amount"
                        defaultDirection="desc"
                      >
                        Invoice amount
                      </SortableTableHead>
                      <SortableTableHead
                        className="text-right"
                        ordering={ordering}
                        onOrderingChange={setOrdering}
                        asc="amount_due"
                        desc="-amount_due"
                        defaultDirection="desc"
                      >
                        Still to pay
                      </SortableTableHead>
                      <SortableTableHead ordering={ordering} onOrderingChange={setOrdering} asc="payment" desc="-payment">
                        Status
                      </SortableTableHead>
                      <th className="h-9 px-3 text-right align-middle text-[11px] font-medium uppercase tracking-[0.06em] text-muted-foreground">
                        Links
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {invLoading && (
                      <tr><td colSpan={7} className="py-8 text-center"><PetanqueLoader size={28} className="mx-auto" /></td></tr>
                    )}
                    {!invLoading && filtered.length === 0 && (
                      <tr><td colSpan={7} className="py-8 text-center text-[13px] text-muted-foreground">
                        {q.trim() ? 'No invoices match your search.' : `No ${invFilter === 'all' ? '' : invFilter} invoices.`}
                      </td></tr>
                    )}
                    {!invLoading && filtered.map((inv) => (
                      <tr key={inv.id} onClick={() => setInvoiceId(inv.id)}
                          className={cn('cursor-pointer border-b border-border last:border-0 hover:bg-muted/50',
                            selectedInv.has(inv.id) && 'bg-primary/5')}>
                        <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                          <input type="checkbox" aria-label={`Select ${inv.name || 'invoice'}`}
                                 checked={selectedInv.has(inv.id)} onChange={() => toggleInv(inv.id)}
                                 className="h-3.5 w-3.5 cursor-pointer rounded border-border accent-[hsl(var(--primary))]" />
                        </td>
                        <td className="px-3 py-2">
                          <div className="font-mono text-[12px]">{inv.name}</div>
                          {inv.ref && <div className="text-[11px] text-muted-foreground">ref {inv.ref}</div>}
                        </td>
                        <td className={cn('px-3 py-2', inv.is_overdue && 'text-rose-600')}>
                          {fmtDate(inv.date_due)}
                          <div className="text-[11px]">{inv.payment_state === 'paid' ? 'paid' : duePhrase(inv.days_overdue)}</div>
                        </td>
                        <td className="px-3 py-2 text-right tnum text-muted-foreground">{money(inv.amount_total, inv.currency)}</td>
                        <td className="px-3 py-2 text-right font-semibold tnum">{money(inv.amount_residual, inv.currency)}</td>
                        <td className="px-3 py-2">
                          {inv.payment_state !== 'not_paid' && <Badge variant="accent">{inv.payment_state}</Badge>}
                          {inv.payment_date && (inv.payment_state === 'in_payment' || inv.payment_state === 'paid') && (
                            <div className="mt-0.5 text-[11px] text-muted-foreground">
                              {inv.payment_state === 'in_payment' ? 'planned ' : 'paid '}{fmtDate(inv.payment_date)}
                            </div>
                          )}
                        </td>
                        <td className="px-2 py-2 text-right text-muted-foreground">
                          <div className="flex items-center justify-end gap-2">
                            {inv.pdf_url && <a href={inv.pdf_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="PDF"><FileText className="h-3.5 w-3.5" /></a>}
                            {inv.odoo_url && <a href={inv.odoo_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} title="Odoo"><ExternalLink className="h-3.5 w-3.5" /></a>}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {invCount > INV_PAGE && (
                <div className="mt-2 flex items-center justify-between text-[12px] text-muted-foreground">
                  <span className="tnum">
                    {invCount === 0 ? 0 : invOffset + 1}–{Math.min(invOffset + INV_PAGE, invCount)} of {invCount}
                  </span>
                  <div className="flex items-center gap-1">
                    <button disabled={invOffset === 0 || invLoading}
                            onClick={() => setInvOffset(Math.max(0, invOffset - INV_PAGE))}
                            className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 disabled:opacity-40 hover:bg-muted">
                      <ChevronLeft className="h-3.5 w-3.5" /> Prev
                    </button>
                    <button disabled={invOffset + INV_PAGE >= invCount || invLoading}
                            onClick={() => setInvOffset(invOffset + INV_PAGE)}
                            className="inline-flex h-7 items-center gap-1 rounded-md border border-border bg-card px-2 disabled:opacity-40 hover:bg-muted">
                      Next <ChevronRight className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
        )}
      </Dialog>

      <InvoiceDetailDialog invoiceId={invoiceId} open={!!invoiceId}
                           onClose={() => setInvoiceId(null)} onChanged={refreshAll} users={users} />
    </>
  );
}
